import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authedFetch, hasStoredSession, resolveApiDomain, useStoredAuth, type StoredAuth } from "nazare-ui/utils";
import { resolveBffDomain } from "./bff-domain";
import { MCP_EVENTS_FLAG, parseRoleNames } from "./notification-channel-visibility";

/**
 * Real BFF client for admin notification management — same
 * `/notifications/admin/users/*` REST contract added in the
 * `notifications-admin` BFF worktree (services/notifications/routes/
 * admin-users.ts), following the same authed-fetch pattern as
 * qa-automation-api.ts.
 */

export type ChannelType = "email" | "whatsapp" | "slack" | "push" | "in_app" | "mcp_event";

/** Every channel a preference can be stored against — mirrors the BFF's
 * `CHANNEL_TYPES` (services/notifications/lib/channels.ts), duplicated here
 * since this is a REST client with no shared-package import path into the
 * BFF's own source. Used as the matrix's fixed column set. */
export const CHANNEL_TYPES: ChannelType[] = ["email", "whatsapp", "slack", "push", "in_app", "mcp_event"];

export type NotificationUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar: string | null;
};

export type NotificationChannel = {
  id: string;
  channel_type: ChannelType;
  address: string;
  label: string | null;
  verified: boolean;
  verified_at: string | number | null;
  created_at: string | number;
};

export type NotificationPreference = {
  id: string;
  notification_type: string;
  reference_id: string | null;
  channel_type: ChannelType;
  enabled: boolean;
};

export type PreferenceEdit = {
  type: string;
  refId: string;
  channel: ChannelType;
  enabled: boolean;
};

const ADMIN_BASE = `${resolveBffDomain()}/notifications/admin`;

const MY_ROLES_QUERY = "query MyRolesV3 { roles: getMyRolesV3 { nodes { id name description } } }";

/** Whether the signed-in user holds the `mcp_events_channel` flag — false
 * until the roles load (and on failure), so the MCP channel stays hidden. */
export function useMcpEventsEnabled(): boolean {
  const auth = useStoredAuth();

  const { data } = useQuery({
    queryKey: ["notifications-admin", "my-roles"],
    queryFn: async (): Promise<string[]> => {
      const res = await authedFetch(resolveApiDomain(window.location.origin, import.meta.env.VITE_NAZARE_API_DOMAIN), auth, "/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: MY_ROLES_QUERY }),
      });
      if (!res.ok) throw new Error(`Failed to fetch roles: ${res.status}`);
      return parseRoleNames(await res.json());
    },
    enabled: hasStoredSession(auth),
    staleTime: 5 * 60_000,
  });

  return data?.includes(MCP_EVENTS_FLAG) ?? false;
}

export { SessionExpiredError, hasStoredSession } from "nazare-ui/utils";

/** Server-side filter/sort params for `GET /admin/users` — `typesParam`/
 * `channelsParam` are already `types=type:include|exclude,...`-shaped
 * comma-joined strings (see index.tsx's `encodeFilterMap`) and `sortParam`
 * is already `"name:asc"`/`"name:desc"`-shaped (see its `SORT_COLUMN_NAME`)
 * — this client passes them straight through as opaque strings rather than
 * re-decoding into a `Map`/enum and re-encoding, since the BFF's query
 * param shape and this app's URL-encoded filter-state shape were
 * deliberately kept identical (see admin-users.ts's own doc comment on
 * `types`/`channels`/`sort`). `null`/empty omits the param entirely so an
 * unfiltered, unsorted request is byte-identical to the pre-filtering
 * `?search=...`-only request this replaces. */
export type UserSearchFilters = {
  types?: string | null;
  channels?: string | null;
  sort?: string | null;
};

async function fetchUsers(
  auth: StoredAuth | null,
  search: string,
  filters: UserSearchFilters,
): Promise<{ users: NotificationUser[] }> {
  const qs = new URLSearchParams();
  if (search.trim()) qs.set("search", search.trim());
  if (filters.types) qs.set("types", filters.types);
  if (filters.channels) qs.set("channels", filters.channels);
  if (filters.sort) qs.set("sort", filters.sort);
  const res = await authedFetch(ADMIN_BASE, auth, `/users${qs.toString() ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error(`Failed to search users: ${res.status}`);
  return res.json();
}

/** Real user search for the admin picker — debounced by the caller, not
 * here. Filtering/sorting now happens server-side (the BFF's `types`/
 * `channels`/`sort` query params — see `UserSearchFilters`), so the
 * returned `users` array IS the matrix's row set for whatever's currently
 * in the URL; index.tsx no longer re-filters/re-sorts it client-side.
 *
 * `filters` (not just `search`) is part of the query key — TanStack Query
 * only refetches when the key changes, so a filter/sort tweak that left the
 * key unchanged would silently keep showing the OLD (differently filtered)
 * result set. `placeholderData: keepPreviousData` (same convention as
 * `qa-automation-api.ts`'s `useQaRunsList`) keeps that stale-but-still-real
 * previous result set ON SCREEN while the new one loads, rather than
 * dropping to the loading skeleton on every filter click — index.tsx's
 * `isFetching && isPlaceholderData` reads exactly this state to show a thin
 * loading bar over the still-visible old rows instead. */
export function useSearchUsers(search: string, filters: UserSearchFilters = {}) {
  const auth = useStoredAuth();
  const typesParam = filters.types ?? null;
  const channelsParam = filters.channels ?? null;
  const sortParam = filters.sort ?? null;
  return useQuery({
    queryKey: ["notifications-admin", "users", search, typesParam, channelsParam, sortParam],
    queryFn: () => fetchUsers(auth, search, filters),
    enabled: hasStoredSession(auth),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export type NotificationChannelStatus = {
  status: string;
  templateName?: string;
  templateId?: string;
};

export type NotificationTypeConfig = {
  type: string;
  label: string;
  description: string;
  channels: {
    email: NotificationChannelStatus;
    whatsapp: NotificationChannelStatus;
    slack: NotificationChannelStatus;
    push: NotificationChannelStatus;
  };
};

/** Canonical notification-type catalogue for the matrix's type selector. */
export function useNotificationTypesConfig() {
  const auth = useStoredAuth();

  return useQuery({
    queryKey: ["notifications-admin", "notification-types"],
    queryFn: async (): Promise<NotificationTypeConfig[]> => {
      const res = await authedFetch(ADMIN_BASE, auth, "/notification-types");
      if (!res.ok) throw new Error(`Failed to fetch notification types: ${res.status}`);
      const data: { types: NotificationTypeConfig[] } = await res.json();
      return data.types;
    },
    enabled: hasStoredSession(auth),
    staleTime: 5 * 60_000,
  });
}

async function fetchUserChannels(
  auth: StoredAuth | null,
  userId: string,
): Promise<{ channels: NotificationChannel[] }> {
  const res = await authedFetch(ADMIN_BASE, auth, `/users/${userId}/channels`);
  if (!res.ok) throw new Error(`Failed to fetch channels: ${res.status}`);
  return res.json();
}

const userChannelsKey = (userId: string) => ["notifications-admin", "channels", userId];

export function useUserChannels(userId: string | null) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: userChannelsKey(userId ?? ""),
    queryFn: () => fetchUserChannels(auth, userId!),
    enabled: hasStoredSession(auth) && !!userId,
  });
}

async function fetchUsersChannelsBulk(
  auth: StoredAuth | null,
  userIds: string[],
): Promise<{ channelsByUser: Record<string, NotificationChannel[]> }> {
  const res = await authedFetch(ADMIN_BASE, auth, `/users/channels/bulk-read`, {
    method: "POST",
    body: JSON.stringify({ userIds }),
  });
  if (!res.ok) throw new Error(`Failed to bulk-fetch channels: ${res.status}`);
  return res.json();
}

const usersChannelsBulkKeyPrefix = ["notifications-admin", "channels-bulk"];

/** One request for every selected user's channels, grouped server-side by
 * `user_id` — backs the matrix's "disable a channel badge the user hasn't
 * actually connected" check. Replaces the previous N-parallel-GETs pattern
 * (one `fetchUserChannels` per user via `useQueries`) with a single bulk
 * read; the return shape (an array positionally aligned with `userIds`,
 * each entry exposing `.data.channels`/`.isLoading`) is kept the same so
 * existing `results[index]` call sites don't need to change. */
export function useUsersChannels(userIds: string[]) {
  const auth = useStoredAuth();
  const query = useQuery({
    queryKey: [...usersChannelsBulkKeyPrefix, ...userIds],
    queryFn: () => fetchUsersChannelsBulk(auth, userIds),
    enabled: hasStoredSession(auth) && userIds.length > 0,
  });
  return useMemo(
    () =>
      userIds.map((userId) => ({
        data: query.data ? { channels: query.data.channelsByUser[userId] ?? [] } : undefined,
        isLoading: query.isLoading,
      })),
    [query.data, query.isLoading, userIds],
  );
}

export function useDeleteChannel(userId: string) {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (channelId: string) => {
      const res = await authedFetch(ADMIN_BASE, auth, `/users/${userId}/channels/${channelId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Failed to delete channel: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userChannelsKey(userId) });
      queryClient.invalidateQueries({ queryKey: usersChannelsBulkKeyPrefix });
    },
  });
}

async function fetchUserPreferences(
  auth: StoredAuth | null,
  userId: string,
): Promise<{ preferences: NotificationPreference[] }> {
  const res = await authedFetch(ADMIN_BASE, auth, `/users/${userId}/preferences`);
  if (!res.ok) throw new Error(`Failed to fetch preferences: ${res.status}`);
  return res.json();
}

const userPreferencesKey = (userId: string) => ["notifications-admin", "preferences", userId];

export function useUserPreferences(userId: string | null) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: userPreferencesKey(userId ?? ""),
    queryFn: () => fetchUserPreferences(auth, userId!),
    enabled: hasStoredSession(auth) && !!userId,
  });
}

async function fetchUsersPreferencesBulk(
  auth: StoredAuth | null,
  userIds: string[],
): Promise<{ preferencesByUser: Record<string, NotificationPreference[]> }> {
  const res = await authedFetch(ADMIN_BASE, auth, `/users/preferences/bulk-read`, {
    method: "POST",
    body: JSON.stringify({ userIds }),
  });
  if (!res.ok) throw new Error(`Failed to bulk-fetch preferences: ${res.status}`);
  return res.json();
}

const usersPreferencesBulkKeyPrefix = ["notifications-admin", "preferences-bulk"];

/** One request for every selected user's preferences, grouped server-side
 * by `user_id` — backs the users×channels matrix. Replaces the previous
 * N-parallel-GETs pattern (one `fetchUserPreferences` per user via
 * `useQueries`) with a single bulk read; the return shape (an array
 * positionally aligned with `userIds`, each entry exposing
 * `.data.preferences`/`.isLoading`) is kept the same so existing
 * `results[index]` call sites don't need to change. A cell edit's cache
 * invalidation (see `useSetPreferenceForUser`/`useBulkApplyPreference`
 * below) invalidates every query under `usersPreferencesBulkKeyPrefix` —
 * TanStack Query's partial key matching covers this exact-userIds-array
 * key regardless of which page/selection produced it. */
export function useUsersPreferences(userIds: string[]) {
  const auth = useStoredAuth();
  const query = useQuery({
    queryKey: [...usersPreferencesBulkKeyPrefix, ...userIds],
    queryFn: () => fetchUsersPreferencesBulk(auth, userIds),
    enabled: hasStoredSession(auth) && userIds.length > 0,
  });
  return useMemo(
    () =>
      userIds.map((userId) => ({
        data: query.data ? { preferences: query.data.preferencesByUser[userId] ?? [] } : undefined,
        isLoading: query.isLoading,
      })),
    [query.data, query.isLoading, userIds],
  );
}

export function useUpdatePreference(userId: string) {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edit: PreferenceEdit) => {
      const res = await authedFetch(
        ADMIN_BASE,
        auth,
        `/users/${userId}/preferences/${edit.type}/${edit.refId}/${edit.channel}`,
        { method: "PUT", body: JSON.stringify({ enabled: edit.enabled }) },
      );
      if (!res.ok) throw new Error(`Failed to update preference: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKey(userId) });
    },
  });
}

/** Same PUT as `useUpdatePreference`, but not bound to one `userId` up
 * front — the matrix's cells span many users in a single mounted component,
 * which the Rules of Hooks rule out calling `useUpdatePreference(userId)`
 * once per row for (a variable-length, user-selected set of rows). Takes
 * `userId` as part of the mutation variables instead. */
export function useSetPreferenceForUser() {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, edit }: { userId: string; edit: PreferenceEdit }) => {
      const res = await authedFetch(
        ADMIN_BASE,
        auth,
        `/users/${userId}/preferences/${edit.type}/${edit.refId}/${edit.channel}`,
        { method: "PUT", body: JSON.stringify({ enabled: edit.enabled }) },
      );
      if (!res.ok) throw new Error(`Failed to update preference: ${res.status}`);
      return res.json();
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKey(userId) });
      queryClient.invalidateQueries({ queryKey: usersPreferencesBulkKeyPrefix });
    },
  });
}

export function useBulkUpdatePreferences(userId: string) {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edits: PreferenceEdit[]) => {
      const res = await authedFetch(ADMIN_BASE, auth, `/users/${userId}/preferences/bulk`, {
        method: "POST",
        body: JSON.stringify({ edits }),
      });
      if (!res.ok) throw new Error(`Failed to bulk-update preferences: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKey(userId) });
    },
  });
}

/** Cross-user companion to `useSetPreferenceForUser` — the matrix's
 * select-users-then-click-a-badge bulk-apply flow. One request applies a
 * single `type`/`refId`/`channel`/`enabled` edit to every `userId` in
 * `userIds`, backed by `POST /admin/users/preferences/bulk-apply`, instead
 * of looping `useSetPreferenceForUser` once per selected user. */
export function useBulkApplyPreference() {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userIds, edit }: { userIds: string[]; edit: PreferenceEdit }) => {
      const res = await authedFetch(ADMIN_BASE, auth, `/users/preferences/bulk-apply`, {
        method: "POST",
        body: JSON.stringify({
          userIds,
          type: edit.type,
          refId: edit.refId,
          channel: edit.channel,
          enabled: edit.enabled,
        }),
      });
      if (!res.ok) throw new Error(`Failed to bulk-apply preference: ${res.status}`);
      return res.json();
    },
    onSuccess: (_data, { userIds }) => {
      for (const userId of userIds) {
        queryClient.invalidateQueries({ queryKey: userPreferencesKey(userId) });
      }
      queryClient.invalidateQueries({ queryKey: usersPreferencesBulkKeyPrefix });
    },
  });
}
